import React, { useState, useEffect } from 'react';
import { STACItem, STACCollection } from '../types/stac';
import { stacApi } from '../services/stacApi';
import { RWANDA_CONFIG } from '../config/rwanda';
import TimeRangeSelector from './TimeRangeSelector';

interface SearchPanelProps {
  selectedProvince: string | null;
  selectedItems: STACItem[];
  onItemsChange: (items: STACItem[]) => void;
  timeRange: { start: Date; end: Date };
  onTimeRangeChange: (range: { start: Date; end: Date }) => void;
}

const SearchPanel: React.FC<SearchPanelProps> = ({
  selectedProvince,
  selectedItems,
  onItemsChange,
  timeRange,
  onTimeRangeChange
}) => {
  const [collections, setCollections] = useState<STACCollection[]>([]);
  const [selectedCollections, setSelectedCollections] = useState<string[]>(['sentinel-2-l2a']);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<STACItem[]>([]);
  const [maxCloudCover, setMaxCloudCover] = useState<number>(20);

  useEffect(() => {
    loadCollections();
  }, []);

  useEffect(() => {
    if (selectedCollections.length > 0) {
      performSearch();
    }
  }, [selectedProvince, selectedCollections, timeRange, maxCloudCover]);

  const loadCollections = async () => {
    try {
      const allCollections = await stacApi.getCollections();
      // Filter to priority collections for Rwanda
      const priorityCollections = allCollections.filter(col => 
        RWANDA_CONFIG.PRIORITY_COLLECTIONS.includes(col.id)
      );
      setCollections(priorityCollections);
    } catch (error) {
      console.error('Failed to load collections:', error);
    }
  };

  const performSearch = async () => {
    setLoading(true);
    try {
      const datetime = stacApi.buildDatetimeString(timeRange.start, timeRange.end);
      
      let results;
      if (selectedProvince) {
        results = await stacApi.searchByProvince(
          selectedProvince,
          selectedCollections,
          datetime,
          100
        );
      } else {
        results = await stacApi.searchByBbox(
          RWANDA_CONFIG.DEFAULT_BBOX,
          selectedCollections,
          datetime,
          100
        );
      }

      // Filter by cloud cover if applicable
      const filteredItems = results.features.filter(item => {
        const cloudCover = item.properties['eo:cloud_cover'];
        return cloudCover === undefined || cloudCover <= maxCloudCover;
      });

      setSearchResults(filteredItems);
      onItemsChange(filteredItems);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
      onItemsChange([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleCollection = (collectionId: string) => {
    setSelectedCollections(prev => 
      prev.includes(collectionId)
        ? prev.filter(id => id !== collectionId)
        : [...prev, collectionId]
    );
  };

  const selectAll = () => {
    onItemsChange(searchResults);
  };

  const clearSelection = () => {
    onItemsChange([]);
  };

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="p-4 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-4">Search Satellite Data</h3>
        
        {/* Collections */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Satellite Collections
          </label>
          <div className="space-y-2 max-h-32 overflow-y-auto scrollbar-thin">
            {collections.map(collection => (
              <label key={collection.id} className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={selectedCollections.includes(collection.id)}
                  onChange={() => toggleCollection(collection.id)}
                  className="mr-2 text-rwanda-blue focus:ring-rwanda-blue"
                />
                <span className="flex-1">{collection.title || collection.id}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Time Range */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Time Range
          </label>
          <TimeRangeSelector
            timeRange={timeRange}
            onTimeRangeChange={onTimeRangeChange}
          />
        </div>

        {/* Cloud Cover Filter */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Max Cloud Cover: {maxCloudCover}%
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={maxCloudCover}
            onChange={(e) => setMaxCloudCover(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        {/* Search Button */}
        <button
          onClick={performSearch}
          disabled={loading}
          className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Searching...' : 'Search Data'}
        </button>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          <div className="flex justify-between items-center mb-3">
            <h4 className="font-medium text-gray-900">
              Results ({searchResults.length} items)
            </h4>
            <div className="space-x-2">
              <button
                onClick={selectAll}
                className="text-xs text-rwanda-blue hover:underline"
              >
                Select All
              </button>
              <button
                onClick={clearSelection}
                className="text-xs text-gray-500 hover:underline"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="space-y-2">
            {searchResults.map(item => (
              <div
                key={item.id}
                className={`p-3 rounded-lg border text-sm cursor-pointer transition-colors ${
                  selectedItems.includes(item)
                    ? 'border-rwanda-blue bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => {
                  if (selectedItems.includes(item)) {
                    onItemsChange(selectedItems.filter(i => i.id !== item.id));
                  } else {
                    onItemsChange([...selectedItems, item]);
                  }
                }}
              >
                <div className="font-medium">{item.collection}</div>
                <div className="text-gray-600">
                  {new Date(item.properties.datetime!).toLocaleDateString()}
                </div>
                {item.properties['eo:cloud_cover'] !== undefined && (
                  <div className="text-gray-500 text-xs">
                    Cloud: {item.properties['eo:cloud_cover']}%
                  </div>
                )}
              </div>
            ))}
          </div>

          {searchResults.length === 0 && !loading && (
            <div className="text-center text-gray-500 py-8">
              <div className="text-4xl mb-2">🛰️</div>
              <div>No satellite data found</div>
              <div className="text-sm mt-1">
                Try adjusting your search criteria
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchPanel;